import * as React from 'react';
import { cva, VariantProps } from 'class-variance-authority';

import { cn } from '@epoch-ui/lib/utils';

const alertBannerVariants = cva(
  'tw-relative tw-w-full tw-rounded-md tw-border tw-p-4 [&>svg~*]:tw-pl-7 [&>.alert-banner-icon~*]:tw-pl-7 [&>svg+div]:tw-translate-y-[-3px] [&>svg]:tw-absolute [&>svg]:tw-left-4 [&>svg]:tw-top-4 [&>svg]:tw-text-foreground',
  {
    variants: {
      variant: {
        default: 'tw-bg-secondary tw-text-foreground',
        destructive:
          'tw-border-destructive/50 tw-bg-destructive tw-text-destructive-foreground dark:tw-border-destructive [&>svg]:tw-text-destructive-foreground',
        success: 'tw-bg-positive tw-text-positive-foreground [&>svg]:tw-text-positive-foreground',
        warning: 'tw-bg-warning tw-text-warning-foreground'
      }
    },
    defaultVariants: {
      variant: 'default'
    }
  }
);

const alertBannerIconVariants = cva(
  'tw-absolute tw-bottom-0 tw-top-0 tw-flex tw-h-full tw-w-4 tw-items-center',
  {
    variants: {
      placement: {
        leading: 'tw-left-4 [&~*]:tw-pl-7',
        trailing: 'tw-right-4 [&~*]:tw-pr-7'
      },
      size: {
        sm: 'tw-w-4',
        md: 'tw-w-5',
        lg: 'tw-w-6'
      }
    },
    compoundVariants: [
      {
        placement: 'leading',
        size: 'md',
        className: '[&~*]:tw-pl-8'
      },
      {
        placement: 'leading',
        size: 'lg',
        className: '[&~*]:tw-pl-9'
      },
      {
        placement: 'trailing',
        size: 'md',
        className: '[&~*]:tw-pr-8'
      },
      {
        placement: 'trailing',
        size: 'lg',
        className: '[&~*]:tw-pr-9'
      }
    ],
    defaultVariants: {
      placement: 'leading',
      size: 'sm'
    }
  }
);

const AlertBanner = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertBannerVariants>
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertBannerVariants({ variant }), className)}
    {...props}
  />
));
AlertBanner.displayName = 'AlertBanner';

const AlertBannerTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, children, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn('tw-mb-2 tw-text-sm tw-font-medium tw-leading-none tw-tracking-tight', className)}
    {...props}
  >
    {children}
  </h5>
));
AlertBannerTitle.displayName = 'AlertBannerTitle';

const AlertBannerDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('tw-text-sm [&_p]:tw-leading-5', className)} {...props} />
));
AlertBannerDescription.displayName = 'AlertBannerDescription';

const AlertBannerIcon = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertBannerIconVariants>
>(({ className, placement, size, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(alertBannerIconVariants({ placement, size }), className)}
    {...props}
  />
));
AlertBannerIcon.displayName = 'AlertBannerIcon';

export { AlertBanner, AlertBannerTitle, AlertBannerDescription, AlertBannerIcon };
